"use client";

import React, { useState, useCallback, useMemo } from "react";
import { ModalContainer, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@west-tennessee-consulting/ui";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useChargeCardOnFile } from "@/lib/hooks/api/quickbooks/recurring-charges";
import type { RecurringCharge } from "@/lib/types/recurring-charges";
import { RecurringChargeMetadataSchema, type RecurringChargeMetadata } from "@west-tennessee-consulting/quickbooks-invoice/types/recurring-charges";
import type { ChargeCardOnFileModalData } from "@/lib/modals/data/modal-data-types";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";

export function ChargeCardOnFileModal() {
  return (
    <ModalContainer<ChargeCardOnFileModalData> modalId={MODAL_IDS.recurringCharges.chargeCardOnFile}>
      {({ isOpen, data: modalData, onClose }) => (
        <ChargeCardOnFileModalContent isOpen={isOpen} modalData={modalData} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

function ChargeCardOnFileModalContent({
  isOpen,
  modalData,
  onClose,
}: {
  isOpen: boolean;
  modalData: ChargeCardOnFileModalData | undefined;
  onClose: () => void;
}) {

  const [customAmount, setCustomAmount] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { chargeCardOnFile } = useChargeCardOnFile();

  // Validate and extract metadata with strong typing using Zod
  const metadata = useMemo(() => {
    if (!modalData?.item?.metadata) {
      // Only a real problem when the modal is actually open with an item that
      // lacks metadata. This component is globally mounted (DetailPageModals),
      // so `modalData` is undefined on every page render (incl. SSR) while the
      // modal is closed — logging there spammed every route for every user.
      if (modalData?.item) {
        console.error("Missing metadata in recurring charge", {
          chargeId: modalData?.chargeId,
        });
      }
      return null;
    }

    try {
      // Validate metadata against schema - throws error if invalid
      const validatedMetadata = RecurringChargeMetadataSchema.parse(modalData.item.metadata);
      
      return {
        amount: modalData.amount ?? validatedMetadata.amount ?? 0,
        currency: modalData.currency ?? validatedMetadata.currency ?? "USD",
        customerName: modalData.customerName ?? validatedMetadata.customerName ?? modalData.item.customer_id ?? "Unknown",
        customerEmail: validatedMetadata.customerEmail,
        cardId: validatedMetadata.cardId, // ← Saved card ID in QBO (persistent)
        tax: validatedMetadata.tax,
        subtotal: validatedMetadata.subtotal,
        quantity: validatedMetadata.quantity,
        plan: validatedMetadata.plan,
        ourPrice: validatedMetadata.ourPrice,
        microsoftPrice: validatedMetadata.microsoftPrice,
        taxPercentage: validatedMetadata.taxPercentage,
        discountPercentage: validatedMetadata.discountPercentage,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Invalid metadata structure";
      console.error("Metadata validation failed:", errorMessage);
      toast.error(`Invalid charge data: ${errorMessage}`);
      return null;
    }
  }, [modalData]);

  const handleClose = useCallback(() => {
    onClose();
    setCustomAmount("");
  }, [onClose]);

  const handleCharge = useCallback(async () => {
    if (!modalData || !metadata) {
      toast.error("Missing required charge information");
      return;
    }

    // Verify cardId exists before attempting charge
    if (!metadata.cardId) {
      toast.error("Card ID is missing. Cannot process charge.");
      return;
    }

    setIsLoading(true);
    try {
      // Use custom amount if provided, otherwise use metadata amount
      const amountToCharge = customAmount
        ? Math.round(parseFloat(customAmount) * 100)
        : metadata.amount;

      if (!amountToCharge || amountToCharge <= 0) {
        toast.error("Please enter a valid amount");
        setIsLoading(false);
        return;
      }

      // Call the charge endpoint
      const result = await chargeCardOnFile(modalData.chargeId);

      if (result.success) {
        toast.success(
          `Successfully charged ${metadata.currency} ${(amountToCharge / 100).toFixed(2)}`
        );
        handleClose();
      } else {
        toast.error(result.error || "Failed to charge card");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to charge card"
      );
    } finally {
      setIsLoading(false);
    }
  }, [modalData, metadata, customAmount, chargeCardOnFile, handleClose]);

  if (!isOpen || !modalData || !metadata) return null;

  const displayAmount = customAmount
    ? parseFloat(customAmount)
    : metadata.amount / 100;

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.recurringCharges.chargeCardOnFile}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Charge Card on File"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Charge Card on File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Customer Info */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              Customer
            </Label>
            <p className="text-sm font-semibold">{metadata.customerName}</p>
            {metadata.customerEmail && (
              <p className="text-xs text-muted-foreground">{metadata.customerEmail}</p>
            )}
          </div>

          {/* Plan Info (if available) */}
          {metadata.plan && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Plan
              </Label>
              <p className="text-sm font-semibold">{metadata.plan}</p>
            </div>
          )}

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-sm font-medium">
              Amount ({metadata.currency})
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder={`${(metadata.amount / 100).toFixed(2)}`}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                disabled={isLoading}
                className="flex-1"
              />
              <span className="text-sm font-semibold text-muted-foreground">
                {displayAmount.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Leave blank to use the default amount from metadata (
              {(metadata.amount / 100).toFixed(2)})
            </p>
          </div>

          {/* Breakdown (if available) */}
          {(metadata.subtotal || metadata.tax) && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 space-y-1">
              {metadata.subtotal && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="font-medium">
                    {metadata.currency} {(metadata.subtotal / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {metadata.tax && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Tax:</span>
                  <span className="font-medium">
                    {metadata.currency} {(metadata.tax / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {metadata.taxPercentage && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Tax Rate:</span>
                  <span className="font-medium">{metadata.taxPercentage}%</span>
                </div>
              )}
            </div>
          )}

          {/* Confirmation Message */}
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              You are about to charge{" "}
              <span className="font-semibold">
                {metadata.currency} {displayAmount.toFixed(2)}
              </span>{" "}
              to the card on file for{" "}
              <span className="font-semibold">{metadata.customerName}</span>.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCharge}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? "Charging..." : "Charge Card"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </ResponsiveModal>
  );
}
